import React, { useState, useEffect, useRef } from 'react';
import { FileSignature, Loader2, X as XIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

// Mar 2, 2026 — Provider workflow win: recent-specialty shortcuts.
// Nurse practitioners in Family Med typically cycle the same 6-8 consult
// specialties (GYN, Ortho, PT, MH/BH, Derm, ENT, Cards, Endo). Instead of
// retyping via window.prompt each time, this bar renders the 10 most-
// recently-referred-to specialties as one-tap chips, backed by
// localStorage per browser profile. First-run provider sees a curated
// default set so the button is useful immediately, without having to
// build history first.
const STORAGE_KEY = 'writeright_recent_specialties';
const MAX_RECENT = 10;
const DEFAULT_SEEDS = ['GYN', 'Ortho', 'PT', 'MH/BH', 'Derm', 'ENT', 'Cards', 'Endo'];

const loadRecent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SEEDS.slice();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_SEEDS.slice();
    return parsed.filter((s) => typeof s === 'string' && s.trim()).slice(0, MAX_RECENT);
  } catch (e) {
    return DEFAULT_SEEDS.slice();
  }
};

const saveRecent = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
  } catch (e) { /* quota exceeded — silent */ }
};

export const AddAnotherReferralBar = ({ isGenerating, hasNotes, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState(loadRecent);
  const [typed, setTyped] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      // Focus the input when the popover opens so the provider can start
      // typing a fresh specialty immediately if none of the chips fit.
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  const promote = (spec) => {
    const s = spec.trim();
    if (!s) return;
    // Move-to-front + dedupe (case-insensitive)
    const lc = s.toLowerCase();
    const next = [s, ...recent.filter((r) => r.toLowerCase() !== lc)].slice(0, MAX_RECENT);
    setRecent(next);
    saveRecent(next);
  };

  const handleAdd = (spec) => {
    const s = (spec || '').trim();
    if (!s) return;
    promote(s);
    onAdd(s);
    setTyped('');
    setOpen(false);
  };

  const removeChip = (e, spec) => {
    e.stopPropagation();
    const next = recent.filter((r) => r !== spec);
    setRecent(next);
    saveRecent(next);
  };

  return (
    <div className="mt-2 flex justify-end" data-testid="add-another-referral-wrap">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px] border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
            disabled={isGenerating || !hasNotes}
            data-testid="add-another-referral-btn"
            title="Adds a new specialty referral without touching your existing note or referrals"
          >
            {isGenerating ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <FileSignature className="w-3 h-3 mr-1.5" />}
            + Add Another Referral
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3 space-y-2" align="end" data-testid="add-referral-popover">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Recent Specialties (tap to add)
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="recent-specialty-chips">
            {recent.length === 0 && (
              <span className="text-xs italic text-muted-foreground">No recents yet — type below to add.</span>
            )}
            {recent.map((spec) => (
              <button
                key={spec}
                type="button"
                onClick={() => handleAdd(spec)}
                className="group inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 hover:border-purple-500/50 transition-colors"
                data-testid={`recent-specialty-${spec}`}
                title={`Add ${spec} referral`}
              >
                <span>{spec}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => removeChip(e, spec)}
                  onKeyDown={(e) => { if (e.key === 'Enter') removeChip(e, spec); }}
                  className="opacity-0 group-hover:opacity-100 hover:text-red-300 cursor-pointer"
                  title="Remove from recents"
                  data-testid={`remove-recent-${spec}`}
                >
                  <XIcon className="w-2.5 h-2.5" />
                </span>
              </button>
            ))}
          </div>
          <div className="pt-2 border-t border-border/60 space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Or type a new specialty
            </div>
            <div className="flex gap-1.5">
              <Input
                ref={inputRef}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(typed); }}
                placeholder="e.g., Rheumatology"
                className="h-8 text-xs flex-1"
                data-testid="add-referral-typed-input"
              />
              <Button
                size="sm"
                onClick={() => handleAdd(typed)}
                disabled={!typed.trim()}
                className="h-8 text-xs"
                data-testid="add-referral-submit"
              >
                Add
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AddAnotherReferralBar;
